<script setup lang="ts">
import { ref, inject, Ref } from "vue";
import { TranslationService } from "../services/translation.service";
import { OverlaysService } from "../services/overlays.service";
import axios from "axios";
import { SITEURL } from "@libTs/cms.globals";
import { EAddressType, IYesNoEnum } from "../ApiInterfaces";
import { EOverlay } from "../EOverlay";
import { IAddressBlock, IOrderCart } from "../interfaces/cart.interfaces";
import { ICsvItemsCart, ICsvUploadResponse } from "../interfaces/csv.interface";

const overlaysSvc = inject<OverlaysService>("overlaysSvc") as OverlaysService;
const t = (inject<TranslationService>("translationSvc") as TranslationService)
    .t;

const { csvFlag } = defineProps({
    csvFlag: {
        type: String,
        required: true,
    },
});

const customerEmail = ref("");
const customerNumber = ref("");
const csvform = ref({
    csvfile: {},
});
const csvfile: Ref<any> = ref({});
const invalidCsvUploadMsg = ref("");

const invalidLinesStr = ref("");
const isOrderForOtherUser = ref(false);
const carts: Ref<any[]> = ref([]);
const billingAddress = ref({
    Default: IYesNoEnum.N,
    AddressType: EAddressType.B,
    AddressId: "CSV",
    AddressName2: "",
    AddressName3: "",
    Street: "",
    StreetNo: "",
    ZipCode: "0000",
    City: "",
    State: "",
    Country: "",
});
const shippingAddress = ref({
    Default: IYesNoEnum.N,
    AddressType: EAddressType.S,
    AddressId: "CSV",
    AddressName2: "",
    AddressName3: "",
    Street: "",
    StreetNo: "",
    ZipCode: "0000",
    City: "",
    State: "",
    Country: "",
});

const isUploading = ref(false);
const isLoading = ref(false);

const ResponseStatus = ref(0);

const uploadFile = () => {
    const data = new FormData();
    const dataCsv = (
        (document.getElementById("csvfile") as HTMLInputElement)
            .files as FileList
    )[0];
    // console.log( data_csv );

    if (!dataCsv) {
        return;
    }

    data.append("csvfile", dataCsv);
    // console.log( data );

    isUploading.value = true;
    customerEmail.value = "";
    customerNumber.value = "";
    invalidCsvUploadMsg.value = "";
    invalidLinesStr.value = "";
    carts.value = [];

    axios
        .post<ICsvUploadResponse>(
            SITEURL + "actions/sapShop/csv/upload",
            data,
            {
                responseType: "json",
                headers: {
                    "Content-Type": undefined,
                    "X-Requested-With": "XMLHttpRequest",
                },
            },
        )
        .then(
            // success
            (response) => {
                // console.log(response.data);
                if (
                    (response.data.exception &&
                        response.data.exception.length) ||
                    !response.data.customerEmail
                ) {
                    console.error(
                        "Exception in CsvController",
                        response.data.exception,
                    );
                    console.warn("invalid customer number in CSV");
                    invalidCsvUploadMsg.value = response.data.exception || "";
                    return;
                }

                customerEmail.value = response.data.customerEmail;
                customerNumber.value = response.data.customerNumber;

                const cartsResponse = response.data.carts;

                if (!cartsResponse) {
                    console.warn("no (grouped) items found in CSV");
                    return;
                }

                for (const key in cartsResponse) {
                    const cart: ICsvItemsCart = {
                        orderReference: key === "default" ? "" : key,
                        orderName: cartsResponse[key].name,
                        currency: cartsResponse[key].currency,
                        itemsValid: [],
                        itemsInvalid: [],
                    };

                    const items = cartsResponse[key].items;
                    if (items.length > 0) {
                        for (const item of items) {
                            if (item.valid) {
                                cart.itemsValid.push(item);
                            } else {
                                cart.itemsInvalid.push(item);
                            }
                        }
                        carts.value.push(cart);
                    }
                }

                invalidLinesStr.value = response.data.invalidLinesStr
                    ? response.data.invalidLinesStr
                    : "";
                isOrderForOtherUser.value =
                    response.data.isDifferentCustomer === true;

                // set billing & shipping address
                if (
                    response.data.shippingAddress &&
                    response.data.shippingAddress.City
                ) {
                    const shippingAddressResponse = response.data
                        .shippingAddress as IAddressBlock;
                    shippingAddressResponse.AddressId = "CSV";
                    shippingAddressResponse.Default = IYesNoEnum.Y;
                    shippingAddressResponse.AddressType = EAddressType.S;

                    if (!shippingAddressResponse.Street) {
                        shippingAddressResponse.Street = "n/a";
                    }

                    shippingAddress.value = shippingAddressResponse;
                }
                if (
                    response.data.billingAddress &&
                    response.data.billingAddress.City
                ) {
                    const billingAddressResponse = response.data
                        .billingAddress as IAddressBlock;
                    billingAddressResponse.AddressId = "CSV";
                    billingAddressResponse.Default = IYesNoEnum.Y;
                    billingAddressResponse.AddressType = EAddressType.B;

                    if (!billingAddressResponse.Street) {
                        billingAddressResponse.Street = "n/a";
                    }

                    billingAddress.value = billingAddressResponse;
                }
            },

            // error
            (response) => {
                console.warn(response);
                csvfile.value = null;
                carts.value = [];

                (document.getElementById("csvfile") as HTMLInputElement).value =
                    "";
            },
        )
        .finally(() => {
            isUploading.value = false;
        });
};

const fileChanged = (e: any) => {
    var file = e.target.files[0] || e.dataTransfer.files[0];
    csvform.value.csvfile = file;

    if (file && file.name.match(/\.csv$/i)) {
        uploadFile();
    } else {
        csvfile.value = null;
    }
};

const itemsAddedToCartCallback = () => {
    overlaysSvc.hideOverlays();
};

const addCsvToCart = () => {
    if (isUploading.value) {
        return;
    }

    if (invalidLinesStr.value.length > 0) {
        const thpStr =
            "---------------\n" +
            "CSV Upload\n\n" +
            invalidLinesStr.value +
            "\n\n" +
            "---------------";

        const event = new CustomEvent("thpValueUpdate", {
            detail: {
                thpStr: thpStr,
                addToCart: true,
                append: true,
            },
        });
        document.dispatchEvent(event);
    }

    let cartsLocal: Array<IOrderCart> = [];
    for (const cart of carts.value) {
        if (cart.itemsValid.length > 0) {
            cartsLocal.push({
                orderReference: cart.orderReference,
                orderName: cart.orderName,
                currency: cart.currency,
                items: cart.itemsValid,
                count: 0, // updated in CtrlCart
                totalPrice: 0, // updated in CtrlCart
            });
        }
    }

    if (
        shippingAddress.value.Default === IYesNoEnum.Y ||
        billingAddress.value.Default === IYesNoEnum.Y
    ) {
        const event = new CustomEvent("setCheckoutShippingBillingAddress", {
            detail: {
                shippingAddress: shippingAddress.value,
                billingAddress: billingAddress.value,
            },
        });
        document.dispatchEvent(event);
    }

    const event = new CustomEvent("addItemsToCartsEvent", {
        detail: {
            carts: cartsLocal,
            customerEmail: customerEmail.value,
            isOrderForOtherUser: isOrderForOtherUser.value,
            itemsAddedToCartCallback: itemsAddedToCartCallback,
        },
    });
    document.dispatchEvent(event);

    csvfile.value = null;
    carts.value = [];
};
</script>

<template>
    <div class="overlay-content content-full-width">
        <h2 class="mb-10 text-white">
            {{ t("CSV_UPLOAD_TITLE") }}
            <span v-if="csvFlag == 'Test'" style="color: red">(Demo)</span>
        </h2>
        <div class="upload-scrollbar">
            <div
                class="col-sm-offset-2 col-sm-8 col-lg-offset-3 col-lg-6"
                @click.stop
            >
                <div class="redactor-content mb-15">
                    <template v-if="csvFlag != 'Ja' && csvFlag != 'Test'">
                        {{ t("CSV_UPLOAD_HINT") }}
                    </template>
                    <div v-else class="text-white">
                        <slot></slot>
                    </div>
                </div>

                <form name="csvform" enctype="multipart/form-data" novalidate>
                    <div class="upload mt-8">
                        <label
                            for="csvfile"
                            class="btn-white text-primary"
                            :class="{
                                'btn-grey disabled': isUploading,
                            }"
                            v-text="
                                csvfile && csvfile.name
                                    ? csvfile.name
                                    : t('PRODUCTS_CSV_UPLOAD')
                            "
                        ></label>
                        <input
                            class="hidden"
                            type="file"
                            fileupload="csvfile"
                            name="csvfile"
                            id="csvfile"
                            accept=".csv"
                            @change="uploadFile"
                        />
                    </div>
                </form>

                <div v-if="invalidCsvUploadMsg" class="pt-4">
                    <!--<span style="color:red;">{{ "CSV_WRONG_CUSTOMER_NUMBER"|t("shop") }}</span>-->
                    <span
                        class="text-red font-bold"
                        v-text="invalidCsvUploadMsg"
                    ></span>
                </div>

                <div v-if="carts.length > 0">
                    <div class="container-fluid mt-8" v-for="cart in carts">
                        <div class="row">
                            <div
                                class="text-white"
                                style="margin-bottom: 0.5em"
                            >
                                <div>
                                    <span class="font-bold mr-2"
                                        >{{
                                            t("PRODUCTS_ORDER_REFERENCE")
                                        }}:</span
                                    >
                                    <span
                                        class="font-bold"
                                        v-text="cart.orderReference"
                                    ></span>
                                </div>
                                <div v-if="cart.orderName">
                                    <span class="font-bold mr-2"
                                        >{{ t("PRODUCTS_ORDER_NAME") }}:</span
                                    >
                                    <span v-text="cart.orderName"></span>
                                </div>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 mb-8">
                            <div class="">
                                <div
                                    class="px-4 flex justify-between items-end mb-2 text-white"
                                >
                                    <div class="font-bold">
                                        {{ t("INVALID_PRODUCTS") }}
                                    </div>
                                    <div class="">
                                        {{ t("QUANTITY") }}
                                    </div>
                                </div>
                                <div
                                    class="px-4"
                                    style="
                                        height: 300px;
                                        border: 1px solid red;
                                        background-color: seashell;
                                    "
                                >
                                    <div
                                        v-for="i in cart.itemsInvalid"
                                        class="px-4 flex justify-between items-center mb-2"
                                    >
                                        <div class="" v-text="i.ItemCode"></div>
                                        <div class="" v-text="i.quantity"></div>
                                    </div>
                                </div>
                            </div>
                            <div class="">
                                <div
                                    class="px-4 flex justify-between items-end mb-2 text-white"
                                >
                                    <div class="font-bold">
                                        {{ t("VALID_PRODUCTS") }}
                                    </div>
                                    <div class="">
                                        {{ t("QUANTITY") }}
                                    </div>
                                </div>
                                <div
                                    class="px-2"
                                    style="
                                        height: 300px;
                                        border: 1px solid green;
                                        background-color: lightgreen;
                                    "
                                >
                                    <div
                                        v-for="i in cart.itemsValid"
                                        class="px-2 flex justify-between items-center mb-2"
                                    >
                                        <div class="" v-text="i.ItemCode"></div>
                                        <div class="" v-text="i.quantity"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <span style="color: red"
                        >* {{ t("PRODUCT_CODE_NOT_FOUND") }}</span
                    ><br />
                    <span style="color: red"
                        >** {{ t("INVALID_QUANTITY") }}</span
                    ><br />
                    <br /><br />
                    <a
                        class="btn-white text-primary disabled"
                        v-if="csvFlag == 'Test'"
                        >{{ t("ADD_ALL_TO_CART") }}</a
                    >
                    <a
                        v-else
                        class="btn-white text-primary"
                        :class="{
                            disabled: isLoading || isUploading,
                            'loading-animation': isLoading,
                        }"
                        @click="addCsvToCart()"
                        >{{ t("ADD_ALL_TO_CART") }}</a
                    >
                </div>
            </div>
        </div>
    </div>
</template>
